import React from 'react';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Truck, 
  Users, 
  Package, 
  Receipt,
  CreditCard, 
  BellRing, 
  ChevronLeft, 
  ChevronRight, 
  Sliders, 
  Store,
  Navigation,
  TrendingUp
} from 'lucide-react';

export default function Sidebar({ 
  isCollapsed = false, 
  setIsCollapsed, 
  onToggleCollapse,
  activeTab, 
  onSelectTab, 
  currentRole, 
  pendingOrdersCount = 1,
  lang = 'mr',
  features = {},
  isMobile = false
}) {
  const handleToggle = () => {
    if (typeof onToggleCollapse === 'function') {
      onToggleCollapse();
    } else if (typeof setIsCollapsed === 'function') {
      setIsCollapsed(!isCollapsed);
    }
  };
  const isMr = lang === 'mr';

  // Delivery Boy: Zero Desktop Sidebar (Uses Mobile Bottom Navigation Bar)
  // Delivery Boy or Mobile Screen: Zero Desktop Sidebar (Uses Mobile App Bottom Navigation)
  if (currentRole === 'DELIVERY' || isMobile) {
    return null;
  }

  // Developer Role
  if (currentRole === 'DEVELOPER') {
    return (
      <aside style={{ width: isCollapsed ? 68 : 230, background: '#0f172a', color: '#ffffff', display: 'flex', flexDirection: 'column', borderRight: '1px solid #1e293b', zIndex: 40 }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between' }}>
          {!isCollapsed && <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#f59e0b' }}>Developer Portal</div>}
          <button onClick={handleToggle} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        <nav style={{ padding: '0.75rem 0.5rem' }}>
          <button onClick={() => onSelectTab('developer_portal')} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', borderRadius: 10, background: '#d97706', color: '#ffffff', border: 'none', cursor: 'pointer', fontWeight: 700, width: '100%' }}>
            <Store size={18} />
            {!isCollapsed && <span>{isMr ? 'टेनंट्स व्यवस्थापन' : 'Tenant Management'}</span>}
          </button>
        </nav>
      </aside>
    );
  }

  // Clean Single-Language Menus with dynamic feature toggles
  const showProductCatalog = Boolean(features?.counter_pos || features?.dairy_byproducts || features?.bakery_products);
  const menuSections = [
    {
      title: isMr ? 'डॅशबोर्ड' : 'Dashboard',
      items: [
        { id: 'dashboard', label: isMr ? 'डॅशबोर्ड' : 'Dashboard', icon: LayoutDashboard }
      ]
    },
    {
      title: isMr ? 'व्यवहार' : 'Transactions',
      items: [
        ...(features?.counter_pos !== false ? [{ id: 'pos', label: isMr ? 'काऊंटर विक्री' : 'Counter POS', icon: ShoppingCart, badge: 'F2' }] : []),
        ...(features?.daily_runsheet !== false ? [{ id: 'runsheet', label: isMr ? 'दैनिक वितरण' : 'Daily Run Sheet', icon: Truck, badge: 'F3' }] : []),
        ...(features?.online_orders !== false ? [{ id: 'online_orders', label: isMr ? 'ग्राहक ऑर्डर्स' : 'Online Orders', icon: BellRing, count: pendingOrdersCount }] : [])
      ]
    },
    {
      title: isMr ? 'मास्टर्स' : 'Masters',
      items: [
        { id: 'customers', label: isMr ? 'ग्राहक नोंदणी' : 'Customer Master', icon: Users },
        ...(showProductCatalog ? [{ id: 'products', label: isMr ? 'उत्पादने व दर' : 'Product Catalog', icon: Package }] : []),
        { id: 'rates', label: isMr ? 'दूध दरपत्रक' : 'Milk Rate Charts', icon: TrendingUp },
        { id: 'lines', label: isMr ? 'लाईन्स व्यवस्थापन' : 'Delivery Lines', icon: Navigation },
        { id: 'users', label: isMr ? 'कर्मचारी व हक्क' : 'User Rights', icon: Users }
      ]
    },
    {
      title: isMr ? 'बिलिंग व वसुली' : 'Billing & Receipts',
      items: [
        { id: 'billing', label: isMr ? 'मासिक खातावही' : 'Monthly Ledger', icon: Receipt },
        { id: 'payments', label: isMr ? 'पेमेंट पावती' : 'Payment Receipts', icon: CreditCard }
      ]
    },
    {
      title: isMr ? 'सेटिंग्ज' : 'Settings',
      items: [
        { id: 'settings', label: isMr ? 'प्रणाली सेटिंग्ज' : 'System Settings', icon: Sliders }
      ]
    }
  ].filter(sec => sec.items.length > 0);

  return (
    <aside style={{
      width: isCollapsed ? 68 : 240,
      background: '#0f172a',
      color: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      borderRight: '1px solid #1e293b',
      userSelect: 'none',
      zIndex: 40,
      flexShrink: 0
    }}>
      <div style={{ padding: '0.85rem', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between', height: 58 }}>
        {!isCollapsed && (
          <div style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#38bdf8' }}>MilkPro Mitra</div>
            <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{isMr ? 'दूध व डेअरी प्रणाली' : 'Dairy Management System'}</div>
          </div>
        )}
        <button onClick={handleToggle} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#cbd5e1', borderRadius: 8, padding: '0.35rem', cursor: 'pointer' }}>
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      <div style={{ padding: '0.5rem', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {menuSections.map((sec, sIdx) => (
          <div key={sIdx}>
            {!isCollapsed && (
              <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 800, color: '#64748b', padding: '0.2rem 0.5rem' }}>
                {sec.title}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sec.items.map(item => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelectTab(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: isCollapsed ? 'center' : 'space-between',
                      padding: '0.55rem 0.65rem', borderRadius: 8, border: 'none',
                      background: isActive ? '#0284c7' : 'transparent', color: isActive ? '#fff' : '#94a3b8',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: isActive ? 700 : 500, width: '100%', textAlign: 'left'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <Icon size={16} color={isActive ? '#fff' : '#94a3b8'} />
                      {!isCollapsed && <span>{item.label}</span>}
                    </div>
                    {!isCollapsed && item.badge && (
                      <span style={{ fontSize: '0.62rem', background: 'rgba(255,255,255,0.15)', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }}>
                        {item.badge}
                      </span>
                    )}
                    {!isCollapsed && item.count !== undefined && item.count > 0 && (
                      <span style={{ fontSize: '0.62rem', background: '#ef4444', color: '#fff', padding: '1px 6px', borderRadius: 999, fontWeight: 800 }}>
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
